// @flow

import type { Dispatch } from 'redux';
import { parse } from 'query-string';
import type {
  ExtraMethods,
  SelectDuration,
  SelectMileage,
  SelectPackage,
  SelectClaimFree,
  SelectColor,
  SetAccessories,
  SetBundle,
  SetExtraContracts,
  ToggleBundle,
  ToggleAccessory,
  ToggleExtraContract,
} from './types';
import type { RawActives } from '../../../types';
import type { Duration } from '../../../types/pdb';

import {
  getDefaultColor,
  getColorById,
  getDefaultPackage,
  getDefaultDuration,
  getDefaultMileage,
  getDurationById,
  getPackage,
  getAccessoryById,
  getExtraContractById,
  getMileageById,
  getClaimFreeById,
  getBundleById,
} from '../../pdb/selectors';

import {
  SELECT_DURATION,
  SELECT_MILEAGE,
  SELECT_CLAIMFREE,
  SELECT_PACKAGE,
  SELECT_COLOR,
  SET_ACCESSORIES_FOR_METHOD,
  SET_BUNDLE,
  SET_EXTRA_CONTRACTS_FOR_METHOD,
  TOGGLE_ACCESSORY,
  TOGGLE_EXTRA_CONTRACT,
  TOGGLE_BUNDLE,
} from './constants';

import {
  getActiveBundleAccessoryId,
  getActiveBundleExtraContractIds,
  getActiveDurationId,
  getActivePackageId,
  getActiveMileageId,
  getActiveColorId,
  packageContainsColor,
  getActiveAccessoriesIds,
  getActiveExtraContractsIds,
} from './selectors';
import { getSearch, getLocation } from '../../router';
import { getActivePdbId } from '../../pdb';
import { updateCart } from '../cart/actions';
import { checkVbmr } from '../vbmr/actions';
import { onConfigurationClick as measureConfigurationClick } from '../../gtm/actions/measurements/onConfigurationClick';

export const onConfigurationClick = (
  section: string,
  selectedConfiguration: any,
  queuedActivesDispatchers: {[index: string]: any[]} = {},
): Function => (dispatch: Function) => {
  // Measure all configuration clicks by default using GTM.
  measureConfigurationClick(section, selectedConfiguration.name);
  // Check if VBMR applies before setting selected configuration.
  dispatch(checkVbmr(section, selectedConfiguration, queuedActivesDispatchers));
};

export const selectDurationByPdbId = (pdbId: string, duration: Duration): SelectDuration => ({
  type: SELECT_DURATION,
  pdbId,
  duration,
});

export const selectDuration = (durationId: number, pdbId?: ?string) => (
  (dispatch: Function, getState: Function) => {
    const selectedPdbId = pdbId || getActivePdbId(getState());
    if (selectedPdbId) {
      const selectedDuration = getDurationById(getState(), selectedPdbId, durationId);
      if (selectedDuration) {
        dispatch(onConfigurationClick('duration', selectedDuration, {
          selectDurationByPdbId: [selectedPdbId, selectedDuration],
          updateCart: [selectedPdbId],
        }));
      }
    }
  }
);

export const selectMileageByPdbId = (pdbId: string, mileage: number): SelectMileage => ({
  type: SELECT_MILEAGE,
  pdbId,
  mileage,
});

export const selectMileage = (mileage: number, pdbId?: ?string) => (
  (dispatch: Function, getState: Function) => {
    const selectedPdbId = pdbId || getActivePdbId(getState());
    if (selectedPdbId) {
      const selectedMileage = getMileageById(getState(), selectedPdbId, mileage);
      if (selectedMileage) {
        dispatch(onConfigurationClick('mileage', selectedMileage, {
          selectMileageByPdbId: [selectedPdbId, mileage],
          updateCart: [selectedPdbId],
        }));
      }
    }
  }
);

export const selectClaimFreeByPdbId = (pdbId: string, claimfree: number): SelectClaimFree => ({
  type: SELECT_CLAIMFREE,
  pdbId,
  claimfree,
});

export const selectClaimFree = (claimFreeId: number, pdbId?: ?string) => (
  (dispatch: Function, getState: Function) => {
    const selectedPdbId = pdbId || getActivePdbId(getState());
    if (selectedPdbId) {
      const selectedClaimFree = getClaimFreeById(getState(), selectedPdbId, claimFreeId);
      if (selectedClaimFree) {
        dispatch(onConfigurationClick('claimfree', selectedClaimFree, {
          selectClaimFreeByPdbId: [selectedPdbId, claimFreeId],
          updateCart: [selectedPdbId],
        }));
      }
    }
  }
);

export const selectColorByPdbId = (pdbId: string, color: number): SelectColor => ({
  type: SELECT_COLOR,
  pdbId,
  color,
});

export const selectColor = (colorId: number, pdbId?: ?string) => (
  (dispatch: Function, getState: Function) => {
    const selectedPdbId = pdbId || getActivePdbId(getState());
    if (selectedPdbId) {
      const selectedColor = getColorById(getState(), selectedPdbId, colorId);
      if (selectedColor) {
        dispatch(onConfigurationClick('color', selectedColor, {
          selectColorByPdbId: [selectedPdbId, colorId],
          updateCart: [selectedPdbId],
        }));
      }
    }
  }
);

export const selectPackageByPdbId = (pdbId: string, pack: number): SelectPackage => ({
  type: SELECT_PACKAGE,
  pdbId,
  pack,
});

export const toggleAccessory = (accessory: number, method: ExtraMethods) => (
  (dispatch: Function, getState: Function) => {
    const pdbId = getActivePdbId(getState());
    if (pdbId) {
      const selectedAccessory = getAccessoryById(getState(), pdbId, accessory);
      if (selectedAccessory) {
        dispatch(onConfigurationClick('accessory', selectedAccessory, {
          toggleAccessoryByPdbId: [pdbId, accessory, method],
          updateCart: [],
        }));
      }
    }
  }
);

export const toggleAccessoryByPdbId = (
  pdbId: string,
  accessory: number,
  method: ExtraMethods,
): ToggleAccessory => ({
  type: TOGGLE_ACCESSORY,
  pdbId,
  accessory,
  method,
});

export const setAccessoriesForMethod = (
  pdbId: string,
  accessories: number[],
  method: ExtraMethods,
): SetAccessories => ({
  type: SET_ACCESSORIES_FOR_METHOD,
  pdbId,
  accessories,
  method,
});

export const toggleExtraContractByPdbId = (
  pdbId: string,
  extraContract: number,
  method: ExtraMethods,
): ToggleExtraContract => ({
  type: TOGGLE_EXTRA_CONTRACT,
  pdbId,
  extraContract,
  method,
});

export const setExtraContractsForMethod = (
  pdbId: string,
  extraContracts: number[],
  method: ExtraMethods,
): SetExtraContracts => ({
  type: SET_EXTRA_CONTRACTS_FOR_METHOD,
  pdbId,
  extraContracts,
  method,
});

export const updatePackageExtra = (
  selectedPdbId: number,
  selectedPackageExtras: number[],
  selector: Function,
  action: Function,
): Function => (
  dispatch: Function,
  getState: Function,
) => {
  const actives = selector(getState());
  actives.forEach((activeId: number) => {
    if (!selectedPackageExtras.includes(activeId)) {
      dispatch(action(selectedPdbId.toString(), activeId, 'selected'));
    }
  });
};

export const updatePackageExtras = (
  selectedPdbId: number,
  selectedPackageExtras: number[],
): Function => (
  dispatch: Function,
) => {
  if (!selectedPdbId || !selectedPackageExtras.length) {
    return;
  }
  dispatch(updatePackageExtra(
    selectedPdbId,
    selectedPackageExtras,
    getActiveAccessoriesIds,
    toggleAccessoryByPdbId,
  ));
  dispatch(updatePackageExtra(
    selectedPdbId,
    selectedPackageExtras,
    getActiveExtraContractsIds,
    toggleExtraContractByPdbId,
  ));
};

export const selectPackage = (packageId: number, pdbId?: ?string) => (
  (dispatch: Function, getState: Function) => {
    const state = getState();
    const selectedPdbId = pdbId || getActivePdbId(state);
    const activeColorId = getActiveColorId(state);
    if (selectedPdbId) {
      const selectedPackage = getPackage(state, packageId, selectedPdbId);
      if (!selectedPackage) {
        throw Error(`A package with id ${packageId} doesn't exist`);
      } else {
        let dispatchers: {
              selectPackageByPdbId?: any[],
              selectColorByPdbId?: any[],
              updateCart?: any[],
            } = {
              selectPackageByPdbId: [selectedPdbId, packageId],
              updatePackageExtras: [selectedPdbId, selectedPackage.extras],
            };
        if (activeColorId && !packageContainsColor(state, packageId, activeColorId)) {
          const color = getDefaultColor(state, selectedPdbId, packageId);
          if (color) {
            dispatchers = Object.assign(dispatchers, {
              selectColorByPdbId: [selectedPdbId, color.id],
            });
          }
        }
        dispatchers = Object.assign(dispatchers, {
          updateCart: [pdbId],
        });
        dispatch(onConfigurationClick('package', selectedPackage, dispatchers));
      }
    }
  }
);

export const toggleExtraContract = (extraContractId: number, method: ExtraMethods) => (
  (dispatch: Function, getState: Function) => {
    const pdbId = getActivePdbId(getState());
    if (pdbId) {
      const selectedExtraContract = getExtraContractById(getState(), pdbId, extraContractId);
      if (selectedExtraContract) {
        dispatch(onConfigurationClick('extra contract', selectedExtraContract, {
          toggleExtraContractByPdbId: [pdbId, extraContractId, method],
          updateCart: [],
        }));
      }
    }
  }
);

export const setActiveBundleExtras = (pdbId: string) => (
  (dispatch: Function, getState: Function) => {
    const state = getState();
    const accessoryIds = getActiveBundleAccessoryId(state);
    const extraContractIds = getActiveBundleExtraContractIds(state);

    dispatch(setAccessoriesForMethod(pdbId, accessoryIds, 'bundle'));
    dispatch(setExtraContractsForMethod(pdbId, extraContractIds, 'bundle'));
  }
);

export const toggleBundleByPdbId = (pdbId: string, bundleId: number): ToggleBundle => ({
  type: TOGGLE_BUNDLE,
  pdbId,
  bundle: bundleId,
});

export const setBundleByPdbId = (pdbId: string, bundleId: number): SetBundle => ({
  type: SET_BUNDLE,
  pdbId,
  bundle: bundleId,
});

export const toggleBundle = (bundleId: number) => (dispatch: Function, getState: Function) => {
  const state = getState();
  const pdbId = getActivePdbId(state);
  if (pdbId) {
    const selectedBundle = getBundleById(getState(), pdbId, bundleId);
    if (selectedBundle) {
      dispatch(onConfigurationClick('bundle', selectedBundle, {
        toggleBundleByPdbId: [pdbId, bundleId],
        setActiveBundleExtras: [pdbId],
        updateCart: [],
      }));
    }
  }
};

export const receiveActivesData = (rawActives: RawActives) => (
  (dispatch: Dispatch<any>, getState: Function) => {
    const state = getState();
    const {
      accessories,
      claim_free,
      color,
      contracts,
      duration,
      extra_bundles,
      mileage,
      package: pdbPackage,
      pdbId,
    } = rawActives;

    if (accessories && accessories.length) {
      dispatch(setAccessoriesForMethod(pdbId, accessories, 'selected'));
    }

    if (claim_free) {
      dispatch(selectClaimFreeByPdbId(pdbId, claim_free));
    }

    if (color) {
      dispatch(selectColorByPdbId(pdbId, color));
    }

    if (contracts && contracts.length) {
      dispatch(setExtraContractsForMethod(pdbId, contracts, 'selected'));
    }

    if (duration) {
      const durationObj = getDurationById(state, pdbId, duration);

      if (durationObj) {
        dispatch(selectDurationByPdbId(pdbId, durationObj));
      }
    }

    if (extra_bundles) {
      dispatch(setBundleByPdbId(pdbId, extra_bundles[0]));
      dispatch(setActiveBundleExtras(pdbId));
    }

    if (mileage) {
      dispatch(selectMileageByPdbId(pdbId, mileage));
    }

    if (pdbPackage) {
      dispatch(selectPackageByPdbId(pdbId, pdbPackage));
    }
  }
);

export const selectDefaultConfiguration = (
  overwrite: boolean = false,
  pdbId?: ?string,
): Function => (
  (dispatch: Dispatch<any>, getState: () => *) => {
    const state = getState();
    const selectedPdbId = pdbId || getActivePdbId(state);
    if (!selectedPdbId) {
      return null;
    }

    const activePackId = getActivePackageId(state, selectedPdbId);
    const pack = getDefaultPackage(state, selectedPdbId);
    const activeDurationId = getActiveDurationId(state, selectedPdbId);
    const duration = getDefaultDuration(state, selectedPdbId);
    const activeMileageId = getActiveMileageId(state, selectedPdbId);
    const mileage = getDefaultMileage(state, selectedPdbId);
    const activeColorId = getActiveColorId(state, selectedPdbId);
    let color;
    if (pack) {
      color = getDefaultColor(state, selectedPdbId, pack.id);
    }

    if ((overwrite || !activePackId) && pack) {
      dispatch(selectPackageByPdbId(selectedPdbId, pack.id || 0));
    }

    if ((overwrite || !activeDurationId) && duration) {
      dispatch(selectDurationByPdbId(selectedPdbId, duration));
    }

    if ((overwrite || !activeMileageId) && mileage) {
      dispatch(selectMileageByPdbId(selectedPdbId, mileage.id));
    }

    if ((overwrite || !activeColorId) && color) {
      dispatch(selectColorByPdbId(selectedPdbId, color.id));
    }

    return dispatch(updateCart());
  }
);

export const selectConfigFromUrl = () => (
  (dispatch: Function, getState: Function) => {
    const state = getState();
    const location = getLocation(state);

    // client side the search can be empty fallback to location.search
    const search = getSearch(state) || location.search;
    const params = parse(search);

    if (params.pkg) {
      // try to set the package based on the URL
      try {
        dispatch(selectPackage(parseInt(params.pkg, 10)));
      } catch (err) {
        console.error(err);
      }
    }
  }
);
